/*
 * ss-locale-suggest.js — the soft suggestion banner.
 *
 * Offers English and USD to a visitor the storefront detected outside Mexico,
 * and never moves anyone. Forced geolocation redirection is what stranded
 * Mexican visitors in August; this is its replacement.
 *
 * The safety property lives on the server, not here. The endpoint handed over
 * by snippets/ss-locale-suggest.liquid carries country[exclude]=MX, so a
 * Mexican visitor's suggestion list comes back empty and the run ends. That is
 * why this file holds no comparison against the Mexican country code: a
 * comparison is a thing that can be wrong, and the exclusion cannot. Removing
 * the exclusion from the endpoint is the one edit that would make this banner
 * behave like the redirect it replaces, so the run refuses an endpoint that
 * lost it.
 *
 * The country name is the only value from a network response that reaches the
 * page. It is appended as a text node and never as markup, which is threat
 * T-02-08. The response's own `name` field is deliberately unread: on the
 * Spanish storefront it comes back in Spanish, and "Estados Unidos" inside an
 * otherwise English sentence reads like a bug.
 *
 * Nothing here navigates. Accept dispatches the ss:intl-country event and then
 * clicks the INTL destination inside <ss-market-toggle>, so the banner and the
 * toggle share one submit path rather than growing two implementations of the
 * riskiest code in this phase (threat T-02-11).
 *
 * Public surface, read by scripts/ss-locale-suggest.test.mjs:
 *
 *   window.SsLocaleSuggest.dismissKey(country, language)
 *   window.SsLocaleSuggest.isDismissed(country, language, now)
 *   window.SsLocaleSuggest.recordDismissal(country, language, now)
 *   window.SsLocaleSuggest.pickSuggestion(payload)
 *   window.SsLocaleSuggest.englishCountryName(handle)
 *   window.SsLocaleSuggest.copyParts(copyUs, copyOther, handle, name)
 *   window.SsLocaleSuggest.run(root, now)
 */

(function () {
  'use strict';

  if (window.SsLocaleSuggest) return;

  var DISMISS_PREFIX = 'ss:locale-suggest:';
  var DISMISS_WINDOW_MS = 90 * 24 * 60 * 60 * 1000;
  var CONFIDENCE_MIN = 0.8;
  var TARGET_LANGUAGE = 'en';
  var COUNTRY_TOKEN = '%%SS_COUNTRY%%';
  var REQUIRED_EXCLUSION = 'country[exclude]=MX';
  var COUNTRY_PATTERN = /^[A-Za-z]{2}$/;
  var CONTAINER = 'ss-locale-suggest';
  var TOGGLE = 'ss-market-toggle';
  var TEXT_ID = 'SsLocaleSuggestText';

  /* ---------- the dismissal store ---------- */

  /*
   * Every access is wrapped. A private-mode browser rejects the write and a
   * rejected write must not break the page: the worst outcome of a failed
   * store is one more ask, and the worst outcome of an unguarded throw is a
   * dead script on a shopping page.
   */
  function store() {
    try {
      return window.localStorage || null;
    } catch (error) {
      return null;
    }
  }

  /*
   * The pair is in the key name on purpose. A visitor who dismissed the US
   * suggestion and then travels gets asked once about the new country instead
   * of being silenced for ninety days by a decision about a different place.
   * The prefix is the shared contract from plan 02-02: the toggle's Mexican
   * destination clears every key under it, which is the explicit reset that
   * research/PITFALLS.md Pitfall 1 requires.
   */
  function dismissKey(country, language) {
    if (typeof country !== 'string' || !COUNTRY_PATTERN.test(country)) return null;
    var lang = typeof language === 'string' && language ? language : TARGET_LANGUAGE;
    return DISMISS_PREFIX + country.toUpperCase() + ':' + lang.toLowerCase();
  }

  function isDismissed(country, language, now) {
    var key = dismissKey(country, language);
    if (!key) return false;
    var box = store();
    if (!box) return false;

    var raw;
    try {
      raw = box.getItem(key);
    } catch (error) {
      return false;
    }
    if (!raw) return false;

    var stamp = parseInt(raw, 10);
    if (!isFinite(stamp) || stamp <= 0) return false;

    var at = typeof now === 'number' ? now : Date.now();
    return at - stamp < DISMISS_WINDOW_MS;
  }

  function recordDismissal(country, language, now) {
    var key = dismissKey(country, language);
    if (!key) return false;
    var box = store();
    if (!box) return false;
    try {
      box.setItem(key, String(typeof now === 'number' ? now : Date.now()));
      return true;
    } catch (error) {
      return false;
    }
  }

  /* ---------- the suggestion reader ---------- */

  function pickSuggestion(payload) {
    if (!payload || !payload.suggestions || !payload.suggestions.length) return null;

    var best = payload.suggestions[0];
    if (!best || !best.parts || !best.parts.country) return null;

    var confidence =
      typeof best.confidence === 'number' ? best.confidence : best.parts.country.confidence;
    if (typeof confidence !== 'number' || confidence < CONFIDENCE_MIN) return null;

    var handle = best.parts.country.handle;
    if (typeof handle !== 'string' || !COUNTRY_PATTERN.test(handle)) return null;

    /*
     * No language part appears today because only Spanish is published. Once
     * English publishes it will carry a handle of its own (research question
     * OPEN-7), and reading it here is what makes that day a no-op.
     */
    var language = TARGET_LANGUAGE;
    var part = best.parts.language;
    if (part && typeof part.handle === 'string' && part.handle) {
      language = part.handle.split('-')[0].toLowerCase();
    }

    return { country: handle.toUpperCase(), language: language };
  }

  /* ---------- the country name ---------- */

  function englishCountryName(handle) {
    if (typeof handle !== 'string' || !COUNTRY_PATTERN.test(handle)) {
      return handle ? String(handle) : '';
    }
    var code = handle.toUpperCase();
    try {
      var names = new Intl.DisplayNames(['en'], { type: 'region' });
      var name = names.of(code);
      return typeof name === 'string' && name ? name : code;
    } catch (error) {
      return code;
    }
  }

  /* ---------- the copy ---------- */

  /*
   * The script never writes a sentence. It receives the dominant-case string
   * whole and the generic string with a token where the country name belongs,
   * and splits on that token. Everything visible comes from a locale key.
   */
  function copyParts(copyUs, copyOther, handle, name) {
    var code = typeof handle === 'string' ? handle.toUpperCase() : '';
    if (code === 'US' && copyUs) return [copyUs];

    var template = copyOther || copyUs || '';
    var at = template.indexOf(COUNTRY_TOKEN);
    if (at === -1) return [template];

    return [
      template.slice(0, at),
      name ? String(name) : code,
      template.slice(at + COUNTRY_TOKEN.length),
    ];
  }

  /* ---------- the two controls ---------- */

  function onAccept(root, best) {
    try {
      document.dispatchEvent(
        new CustomEvent('ss:intl-country', {
          detail: { country: best.country },
          bubbles: false,
        })
      );
    } catch (error) {
      /* An older browser without CustomEvent still gets the click below. */
    }

    var toggle = document.querySelector(TOGGLE);
    var button =
      toggle && typeof toggle.querySelector === 'function'
        ? toggle.querySelector('[data-ss-mode="INTL"]')
        : null;

    if (!button) {
      /*
       * A banner that builds its own localization form is a second
       * implementation of the riskiest code in this phase. A missing toggle
       * means the page is misconfigured, not that the banner should improvise.
       */
      var enRoot = root.getAttribute('data-ss-en-root') || '';
      console.warn(
        'ss-locale-suggest: accept found no <ss-market-toggle> to drive' +
          (enRoot ? ' (English publishes under "' + enRoot + '")' : '') +
          '. The banner does not build its own form, so nothing happened.'
      );
      return;
    }

    button.click();
  }

  function onDismiss(root, best) {
    recordDismissal(best.country, best.language, Date.now());
    if (typeof root.remove === 'function') root.remove();
  }

  /* ---------- the render ---------- */

  function control(role, className, label, onClick) {
    var button = document.createElement('button');
    button.setAttribute('type', 'button');
    button.setAttribute('class', className);
    button.setAttribute('data-ss-role', role);
    button.appendChild(document.createTextNode(label));
    button.addEventListener('click', onClick);
    return button;
  }

  function render(root, best) {
    var parts = copyParts(
      root.getAttribute('data-ss-copy-us') || '',
      root.getAttribute('data-ss-copy-other') || '',
      best.country,
      englishCountryName(best.country)
    );

    var text = document.createElement('p');
    text.setAttribute('class', 'ss-locale-suggest__text');
    text.setAttribute('id', TEXT_ID);
    for (var i = 0; i < parts.length; i += 1) {
      /* Threat T-02-08: the one value from the response, appended as text. */
      text.appendChild(document.createTextNode(parts[i]));
    }

    var actions = document.createElement('div');
    actions.setAttribute('class', 'ss-locale-suggest__actions');
    actions.appendChild(
      control(
        'accept',
        'ss-locale-suggest__accept',
        root.getAttribute('data-ss-copy-accept') || '',
        function () {
          onAccept(root, best);
        }
      )
    );
    actions.appendChild(
      control(
        'dismiss',
        'ss-locale-suggest__dismiss',
        root.getAttribute('data-ss-copy-dismiss') || '',
        function () {
          onDismiss(root, best);
        }
      )
    );

    root.appendChild(text);
    root.appendChild(actions);

    root.setAttribute('role', 'status');
    root.setAttribute('aria-live', 'polite');
    root.setAttribute('aria-labelledby', TEXT_ID);
    root.removeAttribute('hidden');

    root.addEventListener('keydown', function (event) {
      var key = event ? event.key || event.code : '';
      if (key === 'Escape' || key === 'Esc') {
        onDismiss(root, best);
      }
    });

    /*
     * The element is already positioned and out of the flow, so the entrance
     * animates the card and never the page. Nothing here can move CLS.
     */
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(function () {
        root.classList.add('ss-locale-suggest--in');
      });
    } else {
      root.classList.add('ss-locale-suggest--in');
    }
  }

  /* ---------- the run ---------- */

  function run(root, now) {
    if (!root || typeof root.getAttribute !== 'function') return Promise.resolve('no-root');

    /* An automated browser is not a shopper. No fetch, no banner (T-02-10). */
    var nav = window.navigator || {};
    if (nav.webdriver) return Promise.resolve('webdriver');

    var endpoint = root.getAttribute('data-ss-endpoint') || '';
    if (!endpoint || endpoint.indexOf(REQUIRED_EXCLUSION) === -1) {
      return Promise.resolve('no-endpoint');
    }

    var at = typeof now === 'number' ? now : Date.now();

    /*
     * The pre-fetch gate. The endpoint suggests the country the storefront
     * already detected, and the storefront hands that country to the page, so
     * the pair is knowable before the request. A fresh dismissal for it means
     * no request at all, which is the difference between a banner and a nag.
     */
    var detected = window.Shopify && window.Shopify.country ? window.Shopify.country : '';
    if (isDismissed(detected, TARGET_LANGUAGE, at)) return Promise.resolve('dismissed');

    return fetch(endpoint, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(function (response) {
        if (!response || !response.ok) return null;
        return response.json();
      })
      .then(function (payload) {
        if (!payload) return 'error';

        var best = pickSuggestion(payload);
        if (!best) {
          var list = payload.suggestions;
          return list && list.length ? 'low-confidence' : 'empty';
        }

        /* The post-fetch gate, on the pair the server actually suggested. */
        if (isDismissed(best.country, best.language, at)) return 'dismissed';

        render(root, best);
        return 'rendered';
      })
      .catch(function () {
        return 'error';
      });
  }

  /* ---------- the schedule ---------- */

  var booted = false;

  function boot() {
    if (booted) return;
    booted = true;
    var root = document.querySelector(CONTAINER);
    if (!root) return;
    run(root, Date.now());
  }

  /*
   * After first paint, never on parse. One suggestion is worth nothing next to
   * the first render of a shopping page.
   */
  function schedule() {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(boot, { timeout: 2400 });
      return;
    }
    if (document.readyState === 'complete') {
      setTimeout(boot, 0);
      return;
    }
    window.addEventListener('load', function () {
      setTimeout(boot, 0);
    });
  }

  window.SsLocaleSuggest = {
    DISMISS_PREFIX: DISMISS_PREFIX,
    DISMISS_WINDOW_MS: DISMISS_WINDOW_MS,
    CONFIDENCE_MIN: CONFIDENCE_MIN,
    TARGET_LANGUAGE: TARGET_LANGUAGE,
    dismissKey: dismissKey,
    isDismissed: isDismissed,
    recordDismissal: recordDismissal,
    pickSuggestion: pickSuggestion,
    englishCountryName: englishCountryName,
    copyParts: copyParts,
    run: run,
    boot: boot,
  };

  schedule();
})();
